import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { HEADER_NAV_GROUPS, ADMIN_NAV_GROUPS, ROLE_LABELS, NavItem, HeaderNavGroup } from '../../utils/roles';

const IS_ADMIN_SURFACE =
  process.env.REACT_APP_BUILD_TARGET === 'admin'
  || (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.'));
// shadow.* hosts are the intrlock-io SA (V3) surface. Shadow SPA reveals
// features that are still cut-off on CES app.intrlock.io — currently the
// Shop / Sign Shop workflow.
const IS_SHADOW =
  typeof window !== 'undefined' && window.location.hostname.startsWith('shadow.');

// Shadow-only Shop group. Kept off of CES app.intrlock.io until cutover.
const SHADOW_SHOP_GROUP: HeaderNavGroup = {
  label: 'Shop',
  items: [
    { label: 'Sign Shop',        path: '/sign-shop',        icon: 'shopping-cart', check: () => true },
  ],
};

// On shadow builds insert Shop just before the last group (Settings).
const NAV_GROUPS = IS_ADMIN_SURFACE
  ? ADMIN_NAV_GROUPS
  : (IS_SHADOW
    ? [
        ...HEADER_NAV_GROUPS.slice(0, -1),
        SHADOW_SHOP_GROUP,
        HEADER_NAV_GROUPS[HEADER_NAV_GROUPS.length - 1],
      ]
    : HEADER_NAV_GROUPS);
import { Role } from '../../types';
import { NavDropdown } from './NavDropdown';
import { apiList } from '../../api/apiClient';

const icons: Record<string, React.ReactNode> = {
  home: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" /></svg>,
  users: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  building: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5" /></svg>,
  'map-pin': <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  user: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
  'user-cog': <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  cpu: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>,
  shield: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
  'credit-card': <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
  settings: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  phone: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>,
  smartphone: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
  clock: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  'shopping-cart': <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" /></svg>,
  bell: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
  camera: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
  'guest-key': <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>,
};

export const HeaderNav: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = (user?.role || 'customer') as Role;
  const { canView: rbacCanView } = usePermissions();

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<Set<string>>(new Set());
  const headerRef = useRef<HTMLElement>(null);

  // Notification state (moved from TopBar)
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close dropdowns on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenDropdown(null);
        setShowNotifications(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const data = await apiList('notifications');
      data.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setNotifications(data.slice(0, 20));
    } catch (e) {
      console.error('Failed to load notifications:', e);
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const toggleNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    setOpenDropdown(null);
    if (next) fetchNotifications();
  };

  const unreadCount = notifications.filter((n: any) => !n.read).length;

  const getNotifIcon = (type: string) => {
    switch (type) {
      case 'customer_created': return '🏢';
      case 'device_offline': return '🔴';
      case 'device_online': return '🟢';
      case 'subscription_past_due': return '⚠️';
      case 'guest_access': return '🔑';
      case 'call_missed': return '📞';
      default: return '🔔';
    }
  };

  const filterItem = (item: NavItem) => {
    const slug = item.path.replace('/', '');
    const rbacView = rbacCanView(slug);
    if (rbacView !== undefined) return rbacView;
    return item.check(role);
  };

  const toggleMobileSection = (label: string) => {
    setMobileExpanded(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Top-of-screen offsets stack: admin stripe (28px) if applicable.
  const stripeOffset = IS_ADMIN_SURFACE ? 28 : 0;
  return (
    <>
      {IS_ADMIN_SURFACE && (
        <div
          className="fixed left-0 right-0 z-50 flex items-center justify-center text-white text-xs font-semibold tracking-wider uppercase"
          style={{
            backgroundColor: '#059669',
            height: 28,
            top: 0,
            letterSpacing: '0.12em',
          }}
        >
          Admin Mode
        </div>
      )}
      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-40 shadow-lg shadow-black/30"
        style={{ backgroundColor: '#101010', top: stripeOffset }}
      >
        <div className="flex items-center px-3 lg:px-6 h-14 lg:h-28">
          {/* Logo */}
          <NavLink to="/dashboard" className="flex-shrink-0 mr-4 lg:mr-8">
            <div className="inline-block">
              <img
                src="/intrlock-banner-dark.png"
                alt="Intrlock"
                className="block object-contain h-10 lg:h-16 w-auto"
                style={{ margin: 0, padding: 0 }}
              />
              {!IS_SHADOW && (
                <p className="text-white -mt-[10px] lg:-mt-[15px] text-[8px] lg:text-[10px] font-medium text-left pl-[41%] leading-none">
                  cloud-based intercom
                </p>
              )}
            </div>
          </NavLink>

          {/* Desktop nav items */}
          <nav className="hidden lg:flex items-center gap-1 flex-1">
            {NAV_GROUPS.map(group => (
              <NavDropdown
                key={group.label}
                group={group}
                role={role}
                isOpen={openDropdown === group.label}
                onToggle={() => setOpenDropdown(prev => prev === group.label ? null : group.label)}
                onClose={() => setOpenDropdown(null)}
                icons={icons}
              />
            ))}
          </nav>

          {/* Right side controls */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Notifications bell removed from the top-right nav. The
                load/poll hooks (notifications state, fetchNotifications,
                toggleNotifications, notifRef) remain in the file for now
                but no UI surfaces them. */}

            {/* User info */}
            <div className="hidden lg:flex items-center gap-2 pl-2 border-l border-white/10 ml-2">
              <div className="text-right">
                <p className="text-sm text-gray-300 truncate max-w-[140px]">{user?.email || user?.name || 'User'}</p>
                <p className="text-xs text-gray-500">{ROLE_LABELS[role]}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
                title="Sign Out"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed left-0 right-0 bottom-0 lg:hidden overflow-y-auto bg-gray-900 animate-dropdown" style={{ top: 56, zIndex: 45 }}>
            <nav className="p-4 space-y-1">
              {NAV_GROUPS.map(group => {
                // Direct link
                if (group.path) {
                  const isActive = location.pathname === group.path || location.pathname.startsWith(group.path + '/');
                  return (
                    <NavLink
                      key={group.label}
                      to={group.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium ${
                        isActive ? 'bg-brand-500/20 text-white' : 'text-gray-300 hover:bg-white/5'
                      }`}
                    >
                      {group.label}
                    </NavLink>
                  );
                }

                // Expandable group
                const allItems = [
                  ...(group.items || []),
                  ...(group.columns || []).flatMap(c => c.items),
                ].filter(filterItem);

                if (allItems.length === 0) return null;

                const isExpanded = mobileExpanded.has(group.label);

                return (
                  <div key={group.label}>
                    <button
                      onClick={() => toggleMobileSection(group.label)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5"
                    >
                      {group.label}
                      <svg
                        className={`w-4 h-4 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isExpanded && (
                      <div className="ml-4 space-y-0.5 mb-2">
                        {group.columns && group.columns.map(col => (
                          <div key={col.label}>
                            <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{col.label}</p>
                            {col.items.filter(filterItem).map(item => (
                              <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                  `flex items-center gap-3 px-4 py-2 rounded-lg text-sm ${
                                    isActive ? 'bg-brand-500/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
                                  }`
                                }
                              >
                                {icons[item.icon]}
                                {item.label}
                              </NavLink>
                            ))}
                          </div>
                        ))}
                        {group.items && group.items.filter(filterItem).map(item => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-4 py-2 rounded-lg text-sm ${
                                isActive ? 'bg-brand-500/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
                              }`
                            }
                          >
                            {icons[item.icon]}
                            {item.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Mobile user info + logout */}
            <div className="p-4 border-t border-white/10 mt-2">
              <div className="px-4 py-2">
                <p className="text-sm text-gray-300">{user?.email || user?.name || 'User'}</p>
                <p className="text-xs text-gray-500">{ROLE_LABELS[role]}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
